/**
 * Video file validation utilities
 * Prevents non-video files from being uploaded to the video sharing platform
 */

import { VIDEO_FORMATS, VIDEO_MIME_TYPES, FILE_SIZE_LIMITS } from './videoConstants'

// Legacy support - extract MIME types from constants
export const VALID_VIDEO_TYPES = Object.values(VIDEO_MIME_TYPES)

// Use centralized format list
export const VALID_VIDEO_EXTENSIONS = VIDEO_FORMATS.ALL_SUPPORTED

export const VIDEO_ACCEPT_ATTRIBUTE = 'video/*,' + VALID_VIDEO_EXTENSIONS.join(',')

/**
 * Validates if a file is a valid video file
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validateVideoFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'No file provided' }
  }

  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
  const isValidMimeType = VALID_VIDEO_TYPES.includes(file.type)
  const isValidExtension = VALID_VIDEO_EXTENSIONS.includes(fileExtension)

  // Log validation details
  console.log('📹 Video file validation:', {
    filename: file.name,
    mimeType: file.type,
    extension: fileExtension,
    mimeValid: isValidMimeType,
    extensionValid: isValidExtension,
    size: file.size
  })

  // Check both MIME type and extension (some browsers/files may not have proper MIME types)
  if (!isValidMimeType && !isValidExtension) {
    return {
      isValid: false,
      error: `Invalid file type. Please select a video file (MP4, MOV, AVI, MKV, WEBM, etc.). Selected: ${file.type || 'unknown type'} ${fileExtension}`
    }
  }

  // Validate file size (use centralized limit)
  if (file.size > FILE_SIZE_LIMITS.MAX_SIZE_BYTES) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
    return {
      isValid: false,
      error: `File size is ${fileSizeMB}MB. Must be less than ${FILE_SIZE_LIMITS.MAX_SIZE_BYTES / (1024 * 1024 * 1024)}GB.`
    }
  }

  return {
    isValid: true,
    mimeType: file.type,
    extension: fileExtension,
    size: file.size
  }
}

/**
 * Sanitizes video filename by removing problematic characters
 * @param {string} filename - Original filename
 * @returns {string} - Sanitized filename safe for storage and URLs
 */
export const sanitizeVideoFilename = (filename) => {
  // Extract file extension
  const lastDotIndex = filename.lastIndexOf('.')
  const name = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : ''

  // Sanitize filename: remove/replace problematic characters
  const sanitized = name
    // Replace spaces with underscores
    .replace(/\s+/g, '_')
    // Remove quotes, apostrophes, commas, hash, and other problematic chars
    .replace(/['''\"`,#@$%^&*()+=\[\]{}|\\:;\"<>?/~]/g, '')
    // Replace multiple underscores with single underscore
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '')
    // Limit length to 100 characters
    .substring(0, 100)

  // Ensure we have a valid name
  const finalName = sanitized || 'video'

  return finalName + extension
}

/**
 * Gets a user-friendly list of supported video formats
 * @returns {string} - Human readable list of supported formats
 */
export const getSupportedFormatsString = () => {
  return 'MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, 3GP, MPG, MPEG, TS, MTS, M2TS'
}